import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { ArrowSquareOut, CircleNotch, X } from '@phosphor-icons/react';
interface EmbeddedSnakWindowProps {
  url: string;
  title: string;
  /** Phone-shaped for Snaks, browser-shaped for websites, document-shaped for PDFs. */
  shape?: 'phone' | 'browser' | 'document';
  /** Shown instead of a frame, for sites that refuse to be embedded. */
  content?: React.ReactNode;
  onClose: () => void;
}

/** Opens external content (a Snak or a website) in a window above the presentation. */
export function EmbeddedSnakWindow({
  url,
  title,
  shape = 'phone',
  content,
  onClose
}: EmbeddedSnakWindowProps) {
  const document_ = shape === 'document';
  const browser = shape === 'browser' || document_;
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);
  return createPortal(<motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.3
  }} className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-3 p-4 sm:p-8" data-id="element-96" data-mp="3sjio535k0gx">
      <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 cursor-default bg-[rgba(11,21,18,0.65)] focus:outline-none" data-id="element-97" data-mp="3lzh8b30ha2kw" />

      <motion.div role="dialog" aria-modal="true" aria-label={title} initial={{
      opacity: 0,
      scale: 0.94,
      y: 16
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.96,
      y: 10
    }} transition={{
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1]
    }} className={['relative flex flex-col overflow-hidden shadow-[0_60px_120px_-40px_rgba(0,0,0,0.7)]', document_ ? 'h-[min(88vh,1000px)] w-[min(960px,100%)] rounded-[18px] bg-white' : browser ? 'h-[min(84vh,900px)] w-[min(1280px,100%)] rounded-[18px] bg-white' : 'h-[min(86vh,860px)] w-auto max-w-full rounded-[28px] bg-[#0B1512]'].join(' ')} style={browser ? undefined : {
      aspectRatio: '9 / 17'
    }} data-id="element-98" data-mp="3c3eag9cnsq3r">
        {browser && <div className="flex shrink-0 items-center gap-3 border-b border-[rgba(1,51,43,0.08)] px-4 py-2.5" data-id="element-99" data-mp="3sfzkpwoqcoke">
            <div className="flex gap-1.5" aria-hidden="true" data-id="element-100" data-mp="3ej2kj4mq1uqu">
              <span className="h-2.5 w-2.5 rounded-full bg-[rgba(1,51,43,0.14)]" data-id="element-101" data-mp="3iha2g2sx923m" />
              <span className="h-2.5 w-2.5 rounded-full bg-[rgba(1,51,43,0.14)]" data-id="element-102" data-mp="231l09okqvnup" />
              <span className="h-2.5 w-2.5 rounded-full bg-[rgba(1,51,43,0.14)]" data-id="element-103" data-mp="27366ws6or5wg" />
            </div>
            <span className="truncate rounded-full bg-[#F4F2EC] px-3 py-1 font-body text-[10.5px] text-[rgba(1,51,43,0.55)]" data-id="element-104" data-mp="1zjnc7oo2xo81">
              {document_ || content ? title : url.replace(/^https?:\/\//, '')}
            </span>
          </div>}
        {content ? <div className="min-h-0 flex-1" data-id="element-105" data-mp="29fqm5gp6ar5j">{content}</div> : <>
        {!loaded && <div className={['absolute inset-0 flex items-center justify-center', browser ? 'text-[rgba(1,51,43,0.4)]' : 'text-white/60'].join(' ')} data-id="element-106" data-mp="24h5h93yz7jar">
            <CircleNotch size={28} className="animate-spin" aria-label="Loading" data-id="element-107" data-mp="1qiyvgm7ax9rg" />
          </div>}
        <iframe src={document_ ? `${url}#view=FitH` : url} title={title} onLoad={() => setLoaded(true)} allow="autoplay; fullscreen; clipboard-write" allowFullScreen className="relative block min-h-0 w-full flex-1 border-0" data-id="element-108" data-mp="1lryyjgy6mroy" />
          </>}
      </motion.div>

      <div className="relative flex flex-wrap items-center justify-center gap-2" data-id="element-109" data-mp="3sjf1aburnlu9">
        {browser && !document_ && !content && <span className="mr-1 font-body text-[11px] text-white/55" data-id="element-110" data-mp="bowqh5z55vt">
            Page not showing? Some sites block embedding —
          </span>}
        <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex h-9 items-center gap-1.5 rounded-full bg-white/10 px-4 font-body text-[11px] text-white/80 backdrop-blur-sm transition-colors hover:bg-white/20 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary" data-id="element-111" data-mp="a4mlh51k7r2v">
          Open in new tab
          <ArrowSquareOut size={12} weight="bold" aria-hidden="true" data-id="element-112" data-mp="t1nt6xmi05ai" />
        </a>
        <button type="button" onClick={onClose} className="inline-flex h-9 items-center gap-1.5 rounded-full bg-white px-4 font-body text-[11px] font-medium text-background transition-colors hover:bg-white/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary" data-id="element-113" data-mp="2pvimtpmz7lve">
          Close
          <X size={12} weight="bold" aria-hidden="true" data-id="element-114" data-mp="2ii9leyqgs1td" />
        </button>
      </div>
    </motion.div>, document.body);
}